import type {ReactElement} from 'react';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {Text, TextSize} from '@Components/text';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {RadioButtonTheme} from './radio-button.types';
import styles from './radio-button.module.scss';

export interface RadioButtonOption {
  text: string;
  id: string;
  dangerouslySetInnerHTML?: boolean;
  tooltipText?: string;
}

interface RadioButtonProps {
  containerClasses?: string;
  selectedTextClasses?: string;
  selectedButtonClasses?: string;
  buttonClasses?: string;
  textClasses?: string;
  options: RadioButtonOption[];
  onSelectionCallback: (selectedId: string) => void;
  selectionId: string;
  isDisabled?: boolean;
  disabledTooltipText?: string;
  isTextClickable?: boolean;
  theme?: RadioButtonTheme;
}

export function RadioButton({
  containerClasses,
  selectedTextClasses,
  options,
  selectedButtonClasses,
  buttonClasses,
  textClasses,
  onSelectionCallback,
  selectionId,
  isDisabled = false,
  disabledTooltipText = '',
  isTextClickable = false,
  theme = RadioButtonTheme.DEFAULT,
}: RadioButtonProps): ReactElement<any> {
  const handleInputChange = (index: number): void => {
    const optionForIndex = options[index];
    onSelectionCallback(optionForIndex.id);
  };

  const getButtonClasses = (): string => {
    return isDisabled ? styles.disabled : '';
  };

  const getRadioButton = (isSelected: boolean): ReactElement<any> => {
    const themeClasses = theme === RadioButtonTheme.DANGER ? styles.danger : '';
    return (
      <div className={`${styles.circleWrapper} ${themeClasses} ${buttonClasses} ${isSelected ? `${styles.selected} ${selectedButtonClasses}` : ''}`}>
        <div className={`${styles.innerCircle} ${isSelected ? styles.selected : ''} `} />
      </div>
    );
  };

  const getButtonForIndex = (index: number): ReactElement<any> => {
    const optionForIndex = options[index];
    return (
      <div key={`wizard-radio-button-${index}`} className={styles.optionGroup}>
        <ClickableDiv
          onClick={() => {
            if (isDisabled) {
              return;
            }
            handleInputChange(index);
          }}
          className={getButtonClasses()}
        >
          {getRadioButton(selectionId === optionForIndex.id)}
        </ClickableDiv>
        <Text
          className={`${styles.textClasses} ${textClasses} ${selectionId === optionForIndex.id ? `${styles.selected} ${selectedTextClasses}` : ''} ${isDisabled ? styles.disabled : ''} ${isTextClickable ? styles.clickableText : ''}`}
          bold={selectionId === optionForIndex.id}
          size={TextSize.XSMALL}
          val={optionForIndex.text}
          dangerouslySetInnerHTML={optionForIndex.dangerouslySetInnerHTML ?? false}
          onClick={() => {
            if (isDisabled || !isTextClickable) {
              return;
            }
            handleInputChange(index);
          }}
        />
      </div>
    );
  };

  const getRadioButtons = (): ReactElement<any>[] => {
    const buttons: ReactElement<any>[] = [];

    for (let i = 0; i < options.length; i++) {
      if (options[i].tooltipText) {
        buttons.push(
          <OverlayTrigger
            key={options[i].id}
            trigger={['hover', 'focus']}
            placement="top"
            delay={{show: 50, hide: 100}}
            overlay={getOverlayForOption(options[i].id, options[i].tooltipText)}
          >
            {getButtonForIndex(i)}
          </OverlayTrigger>
        );
      } else {
        buttons.push(getButtonForIndex(i));
      }
    }

    return buttons;
  };

  const getOverlayForTrigger = (): ReactElement<any> => {
    return (
      <Tooltip id="media-type-radio-tooltip" className="spacing-m-b-3 _pointer-events-none">
        {disabledTooltipText}
      </Tooltip>
    );
  };

  const getOverlayForOption = (id: string, tooltipText: string | undefined): ReactElement<any> => {
    return (
      <Tooltip id={`${id}-tooltip`} className="spacing-m-b-3 _pointer-events-none">
        {tooltipText}
      </Tooltip>
    );
  };

  const wrapInTooltipIfNeeded = (): ReactElement<any> => {
    const buttonGroup = <div className={`${styles.radioButtonWrapper} ${containerClasses} ${isDisabled ? styles.disabled : ''}`}>{getRadioButtons()}</div>;

    if (isDisabled && disabledTooltipText !== '') {
      return (
        <OverlayTrigger trigger={['hover', 'focus']} placement="top" delay={{show: 50, hide: 100}} overlay={getOverlayForTrigger()} key="media-type-radio-pverlay" offset={[0, -5]}>
          {buttonGroup}
        </OverlayTrigger>
      );
    }

    return buttonGroup;
  };

  return wrapInTooltipIfNeeded();
}
